import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input — pt-BR-friendly numeric handling:
 *  - For type="number": renders as type="text" with inputMode="decimal" so the
 *    browser does NOT reject commas (native number inputs swallow "," before
 *    onChange ever fires on most engines). We then sanitize on the fly,
 *    replacing comma with dot and dropping invalid chars, so consumers can
 *    keep using parseFloat(e.target.value) unchanged.
 *  - For step="any" semantics on decimal-friendly inputs, we default to "any"
 *    when the consumer omits it.
 */
const Input = React.forwardRef(({ className, type, inputMode, onChange, step, ...props }, ref) => {
  const isNumber = type === "number";

  const handleChange = React.useCallback((e) => {
    if (isNumber) {
      const raw = e.target.value || "";
      // Allow: digits, single sign at start, single decimal separator. Convert , -> .
      let v = raw.replace(/,/g, ".").replace(/[^0-9.\-]/g, "");
      // collapse multiple dots: keep only the first
      const firstDot = v.indexOf(".");
      if (firstDot !== -1) {
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
      }
      // sign only valid at position 0
      if (v.length > 0) {
        const sign = v[0] === "-" ? "-" : "";
        v = sign + v.slice(sign ? 1 : 0).replace(/-/g, "");
      }
      if (v !== raw) {
        e.target.value = v;
      }
    }
    onChange?.(e);
  }, [isNumber, onChange]);

  // Render number inputs as text+inputMode=decimal so commas are accepted on all platforms.
  const renderType = isNumber ? "text" : type;
  const renderInputMode = inputMode || (isNumber ? "decimal" : undefined);

  return (
    <input
      type={renderType}
      inputMode={renderInputMode}
      step={isNumber && step === undefined ? "any" : step}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onChange={handleChange}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
